import { useEffect, useState } from "react";
import Footer from "../components/Footer";
import {
  ADMIN_ACCESS,
  PRICE_ADMIN_ACCESS,
  PRICE_TIME_ADMIN_ACCESS,
  USERS_ADMIN_ACCESS,
} from "../utils/constants";
import { ExternalLink } from "../components/ExternalLink";
import { useSelector, useDispatch } from "react-redux";
import { selectUserRoles } from "../redux/reducers/auth.reducer";
import { Spinner } from "../components/Spinner";
import { hasSuperAdminAccess, verifyModuleAccess } from "../utils/accessUtils";
import { ForbiddenPage } from "./ForbiddenPage";
import { UsersService } from "../../UsersModule/services/users";
import { addToast } from "../redux/reducers/ui.reducer";

export const AdminPage = () => {
  const dispatch = useDispatch();
  const { rolesLoaded, userRolesList } = useSelector(selectUserRoles);

  const [pageLoader, setPageLoader] = useState(false);

  const url = process.env.REACT_APP_ASUITE_BASE_URI;
  const rolesUrl: any = process.env.REACT_APP_APPSMITH_ROLES_URL;
  const priceLevelsUrl: any = process.env.REACT_APP_APPSMITH_PRICE_LEVEL_URL;
  const userPriceLevelsUrl: any =
    process.env.REACT_APP_APPSMITH_USER_PRICE_LEVEL_URL;
  const clientPriceLevelsUrl: any =
    process.env.REACT_APP_APPSMITH_CLIENT_PRICE_LEVEL_URL;
  const clientPriceExceptionsUrl: any =
    process.env.REACT_APP_APPSMITH_CLIENT_PRICE_EXCEPTION_URL;
  const projectPriceExceptionsUrl: any =
    process.env.REACT_APP_APPSMITH_PROJECT_PRICE_EXCEPTION_URL;
  const iCalFeedUrl: any = process.env.REACT_APP_APPSMITH_ICAL_FEED_URL;
  const costCentersUrl: any = process.env.REACT_APP_APPSMITH_COST_CENTERS_URL;
  const taskTypesUrl: any = process.env.REACT_APP_APPSMITH_TASK_TYPES_PAGE;
  const nonBillableReasonsUrl: any =
    process.env.REACT_APP_APPSMITH_NON_BILLABLE_REASONS_PAGE;

  useEffect(() => {
    document.title = "The Hub - Admin";
  }, []);

  const goToAsuiteAdmin = () => {
    window.location.href = url + "/admin/adminmenu.asp";
  };

  const syncUsers = async () => {
    setPageLoader(true);
    await UsersService.importAzureUsers()
      .then((response) => {
        dispatch(
          addToast({
            mode: response.success ? "success" : "error",
            message: response.message,
          })
        );
        setPageLoader(false);
      })
      .catch((err) => {
        dispatch(addToast({ mode: "error", message: err.message }));
        setPageLoader(false);
      });
  };

  return (
    <>
      {!rolesLoaded && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {rolesLoaded &&
        !verifyModuleAccess(true, ADMIN_ACCESS, userRolesList) && (
          <ForbiddenPage />
        )}

      {rolesLoaded && verifyModuleAccess(true, ADMIN_ACCESS, userRolesList) && (
        <div className="content">
          <div className="content-header" style={{ marginTop: "0" }}>
            <h4 className="admin-title">The Hub Administration</h4>
            <button
              className="btn btn-primary button-link"
              onClick={() => goToAsuiteAdmin()}
              disabled={pageLoader}
            >
              ASuite Admin
            </button>
          </div>

          {pageLoader && (
            <Spinner
              style={{
                marginLeft: "50%",
                marginTop: "5%",
              }}
            />
          )}

          {!pageLoader && (
            <div className="admin-container">
              <div className="left-column">
                <h5 className="fw-600">Users</h5>
                {hasSuperAdminAccess(userRolesList) && (
                  <ExternalLink className="link-text" to={rolesUrl}>
                    Assign Roles to Users
                  </ExternalLink>
                )}
                {verifyModuleAccess(
                  true,
                  PRICE_TIME_ADMIN_ACCESS,
                  userRolesList
                ) && (
                  <ExternalLink className="link-text" to={userPriceLevelsUrl}>
                    Assign Price Level to Users
                  </ExternalLink>
                )}
                {verifyModuleAccess(
                  true,
                  USERS_ADMIN_ACCESS,
                  userRolesList
                ) && (
                  <button
                    className="button-action-no-styles link-text"
                    onClick={() => syncUsers()}
                  >
                    Sync Users with Active Directory
                  </button>
                )}
                {verifyModuleAccess(
                  true,
                  PRICE_ADMIN_ACCESS,
                  userRolesList
                ) && (
                  <>
                    <h5 className="fw-600 mt-4">Clients</h5>
                    <ExternalLink
                      className="link-text"
                      to={clientPriceLevelsUrl}
                    >
                      Assign Price Schedule to Clients
                    </ExternalLink>
                    <ExternalLink
                      className="link-text"
                      to={clientPriceExceptionsUrl}
                    >
                      Client Price Exceptions
                    </ExternalLink>
                  </>
                )}
                {hasSuperAdminAccess(userRolesList) && (
                  <>
                    <h5 className="fw-600 mt-4">Time</h5>
                    <ExternalLink className="link-text" to={taskTypesUrl}>
                      Task Types
                    </ExternalLink>
                    <ExternalLink
                      className="link-text"
                      to={nonBillableReasonsUrl}
                    >
                      Non-Billable Reasons
                    </ExternalLink>
                  </>
                )}
                {verifyModuleAccess(
                  true,
                  PRICE_ADMIN_ACCESS,
                  userRolesList
                ) && (
                  <>
                    <h5 className="fw-600 mt-4">Projects</h5>
                    <ExternalLink
                      className="link-text"
                      to={projectPriceExceptionsUrl}
                    >
                      Project Price Exceptions
                    </ExternalLink>
                  </>
                )}
              </div>
              <div className="right-column">
                {verifyModuleAccess(
                  true,
                  PRICE_ADMIN_ACCESS,
                  userRolesList
                ) && (
                  <>
                    <h5 className="fw-600 mt-4">Administration</h5>
                    <ExternalLink className="link-text" to={priceLevelsUrl}>
                      Price Levels
                    </ExternalLink>
                    {hasSuperAdminAccess(userRolesList) && (
                      <>
                        <ExternalLink className="link-text" to={costCentersUrl}>
                          Cost Centers
                        </ExternalLink>
                        <ExternalLink className="link-text" to={iCalFeedUrl}>
                          iCal Feed
                        </ExternalLink>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          <Footer />
        </div>
      )}
    </>
  );
};
